import { EuiFieldSearch, EuiFlexItem, EuiSuperSelect, EuiLoadingContent } from '@elastic/eui';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import APIV1 from '../../../api/axios/APIV1';
import { toastsErrorMessage, toastSuccessMessage } from '../../../utils/toasts';
import { useDispatch, useSelector } from 'react-redux';
import OutletsDataGrid from '../Components/OutletsDataGrid';
import { filterSubscriptionOptions } from '../../Customers/Utils/utils';

const ListOutletsScreen = () => {
    const dispatch = useDispatch();
    const selectedRestaurantId = useSelector((state) => state.auth.selectedRestaurantId);

    const [activePage, setActivePage] = useState(0);
    const [rowSize, setRowSize] = useState(10);
    const [selectedFilterColumn, setSelectedFilterColumn] = useState(filterSubscriptionOptions[0].value);

    const onPageChange = (value) => {
        setActivePage(value);
    };

    const onRowSizeChange = (value) => {
        setRowSize(value);
    };

    const [allOutlets, setAllOutlets] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const isMounted = useRef(true); // Create a ref to track mount status

    useEffect(() => {
        return () => {
            isMounted.current = false; // Set to false when component unmounts
        };
    }, []);

    const fetchOutletInfo = useCallback(
        async ({ query = '', filterColumn = 'paid', size = rowSize, page = activePage } = {}) => {
            if (!selectedRestaurantId) {
                setIsLoading(false);
                return;
            }

            let response = null;
            setIsLoading(true);

            try {
                const payload = {
                    status: filterColumn === 'AllOutlets' ? null : filterColumn,
                    page: page + 1,
                    size: size,
                };

                if (query) {
                    payload.search_keyword = query;
                }
                response = await APIV1.get(`/subscription-requests?restaurant_id=${selectedRestaurantId}`, {
                    params: payload,
                });
                setIsLoading(false);
            } catch (error) {
                response = error;
                setIsLoading(false);
            }

            if (response && response.success) {
                setAllOutlets(response);
            } else {
                toastsErrorMessage('Error fetching subscription requests', dispatch);
                setAllOutlets(null);
            }
        },
        [dispatch, selectedRestaurantId, activePage, rowSize]
    );

    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');

    // Debounce search text
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchText]);

    // Single effect to handle all API calls
    useEffect(() => {
        fetchOutletInfo({
            query: debouncedSearchText,
            page: debouncedSearchText ? 0 : activePage, // Reset to page 0 when searching
            size: rowSize,
            filterColumn: selectedFilterColumn,
        });
    }, [fetchOutletInfo, debouncedSearchText, activePage, rowSize, selectedFilterColumn]);

    const onChangeSerachText = (value) => {
        setSearchText(value);
        if (value && activePage !== 0) {
            setActivePage(0);
        }
    };

    const onColumnFilterChange = (value) => {
        setSelectedFilterColumn(value);
    };

    const handleDeleteOutlet = useCallback(
        async (outletId) => {
            if (!outletId) return;
            if (window.confirm('Are you sure you want to delete this outlet ?')) {
                try {
                    const response = await APIV1.delete(`/subscription-requests/${outletId}`);
                    if (response && response.success) {
                        toastSuccessMessage('Outlet deleted successfully', dispatch);
                        // Refresh the list after deletion
                        fetchOutletInfo({
                            query: debouncedSearchText,
                            page: activePage,
                            size: rowSize,
                            filterColumn: selectedFilterColumn,
                        });
                    } else {
                        toastsErrorMessage('Error deleting outlet', dispatch);
                    }
                } catch (error) {
                    toastsErrorMessage('Error deleting outlet', dispatch);
                }
            }
        },
        [dispatch, fetchOutletInfo, debouncedSearchText, selectedFilterColumn, activePage, rowSize]
    );

    if (isLoading) {
        return <EuiLoadingContent lines={10} />;
    }

    return (
        <>
            <EuiFlexItem
                style={{
                    paddingBottom: '10px',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '90%',
                    marginBottom: '10px',
                    gap: '30px',
                }}
            >
                <div style={{ width: '25%' }}>
                    <b style={{ fontSize: '12px' }}>Search by</b>
                    <EuiFieldSearch
                        placeholder='Search by outlet name or status'
                        value={searchText}
                        onChange={(e) => onChangeSerachText(e.target.value)}
                        isClearable={true}
                        aria-label='search outlet'
                    />
                </div>
                <div style={{ width: '25%' }}>
                    <b style={{ fontSize: '12px' }}>Sort by Column</b>
                    <EuiSuperSelect
                        options={filterSubscriptionOptions}
                        valueOfSelected={selectedFilterColumn}
                        onChange={(value) => onColumnFilterChange(value)}
                        fullWidth={true}
                    />
                </div>
            </EuiFlexItem>
            <EuiFlexItem>
                <OutletsDataGrid
                    getAllOutlets={allOutlets}
                    activePage={activePage}
                    onPageChange={onPageChange}
                    rowSize={rowSize}
                    onRowSizeChange={onRowSizeChange}
                    onDeleteOutlet={handleDeleteOutlet}
                    totalPages={allOutlets?.subscription_requests?.last_page || 0}
                    onRefresh={() =>
                        fetchOutletInfo({
                            query: debouncedSearchText,
                            page: activePage,
                            size: rowSize,
                            filterColumn: selectedFilterColumn,
                        })
                    }
                />
            </EuiFlexItem>
        </>
    );
};

export default ListOutletsScreen;
